import React, { useContext, useEffect, useState } from 'react';
import numeral from 'numeral';
import { useConfirm } from 'material-ui-confirm';
import parse from 'html-react-parser';

import { AppContext } from '../../../context';
import { translate as t } from 'hooks/useTranslations';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import PropertyList from 'components/_layouts/PropertyList';
import datetimeHelper from 'helpers/datetimeHelper';
import { Box, Button, Link, Typography } from '@mui/material';
import { ActionMenu } from 'components/_atoms';
import { PhxBadge } from '_lex/_atoms/PhxBadge';
import useOrganization from 'views/App/Portal/LexID/Organizations/_data_sources/useOrganization';
import useManageOrganization from 'views/App/Portal/LexID/Organizations/_data_sources/useManageOrganization';
import { enqueueSnackbar } from 'notistack';
import Loading from '../../Loading';

export default function Settings() {
  const { currentOrganizationId, hasPermission, setCurrentModule, updatePermissions } = useContext(AppContext);

  const confirm = useConfirm();
  const [processingEnforceAuth, setProcessingEnforceAuth] = useState<boolean>(false);
  const { enforceAuth } = useManageOrganization();
  const organizationQuery = useOrganization(currentOrganizationId);

  const handleEnforce = (authenticationType: string) => {
    confirm({
      description: t(`organizations.actions.auth.confirm.${authenticationType}`),
    })
      .then(() => {
        setProcessingEnforceAuth(true);
        enforceAuth(currentOrganizationId, authenticationType, (jobId) => {
          enqueueSnackbar(parse(t(`organizations.actions.auth.confirmation.${authenticationType}`)), {
            variant: 'success',
            autoHideDuration: 6000,
          });
          setProcessingEnforceAuth(false);
        });
      })
      .catch(() => {
        // don't do it
      });
  };

  const actions = [];
  if (currentOrganizationId !== 'LexipolGenesisRoot') {
    if (currentOrganizationId && hasPermission('updateOrg', currentOrganizationId)) {
      actions.push(
        <Button
          key='edit_org'
          variant='contained'
          size='small'
          href='./edit'
        >
          {t('organizations.actions.edit')}
        </Button>
      );
    }
    if (
      currentOrganizationId &&
      (hasPermission('updateOrgSSO', currentOrganizationId) ||
        hasPermission('updateAllUsersAuthFlow', currentOrganizationId))
    ) {
      actions.push(
        <ActionMenu
          key='auth_actions'
          id='auth_actions'
          btnProps={{
            size: 'small',
          }}
          btnText={t('organizations.actions.auth.button')}
          items={[
            ...(hasPermission('updateOrgSSO', currentOrganizationId) &&
            currentOrganizationId !== 'lexipoladminstaffuuid'
              ? [
                  {
                    key: 'update_auth',
                    label: t('organizations.actions.auth.update'),
                    href: './auth',
                  },
                ]
              : []),
            ...(hasPermission('updateAllUsersAuthFlow', currentOrganizationId) &&
            currentOrganizationId !== 'lexipoladminstaffuuid'
              ? [
                  {
                    key: 'enforce_saml',
                    label: t('organizations.actions.auth.enforceSAML'),
                    onClick: () => handleEnforce('SAML'),
                  },
                  {
                    key: 'enforce_oidc',
                    label: t('organizations.actions.auth.enforceOIDC'),
                    onClick: () => handleEnforce('OIDC'),
                  },
                  {
                    key: 'enforce_password',
                    label: t('organizations.actions.auth.enforcePassword'),
                    onClick: () => handleEnforce('PASSWORD'),
                  },
                ]
              : []),
          ]}
        />
      );
    }
  }

  useEffect(() => {
    setCurrentModule('lexid');
  }, [setCurrentModule]);

  useEffect(() => {
    if (organizationQuery.data?.organization?.authorizations) {
      updatePermissions(
        currentOrganizationId,
        organizationQuery.data.organization.authorizations.map((x) => x.action)
      );
    }
  }, [organizationQuery.data, currentOrganizationId, updatePermissions]);

  return (
    <CommonLayout
      headerProps={{
        title: t('organizations.settings.title'),
        subtitle:
          organizationQuery.data?.organization?.organizationType === 'TEST' ? (
            <PhxBadge color='info'>Test Organization</PhxBadge>
          ) : null,
        actions: actions,
      }}
    >
      {processingEnforceAuth && <Loading />}
      <Box>
        <PropertyList
          columns={3}
          items={
            organizationQuery.data
              ? [
                  {
                    label: t('organizations.properties.salesforceId'),
                    value: organizationQuery.data?.organization?.salesforceId,
                  },
                  {
                    label: t('organizations.properties.userCount'),
                    value: numeral(organizationQuery.data?.organization?.userCount).format('0,0'),
                  },
                  {
                    label: t('organizations.properties.roleCount'),
                    value: numeral(organizationQuery.data?.organization?.roleCount).format('0,0'),
                  },
                  {
                    label: t('organizations.properties.groupCount'),
                    value: numeral(organizationQuery.data?.organization?.groupCount).format('0,0'),
                  },
                  {
                    label: t('organizations.properties.authenticationType'),
                    value: t(
                      `organizations.authenticationTypes.${organizationQuery.data?.cognitoPool?.authenticationType}`
                    ),
                  },
                  {
                    label: t('organizations.properties.identifier'),
                    value: organizationQuery.data?.cognitoPool?.urn,
                    copyable: true,
                    copyValue: organizationQuery.data?.cognitoPool?.urn,
                  },
                  {
                    label: t('organizations.properties.replyUrl'),
                    value: organizationQuery.data?.cognitoPool?.replyUrl ? (
                      <Link
                        href={organizationQuery.data?.cognitoPool?.replyUrl}
                        target='_blank'
                      >
                        {organizationQuery.data?.cognitoPool?.replyUrl}
                      </Link>
                    ) : null,
                    copyable: true,
                    copyValue: organizationQuery.data?.cognitoPool?.replyUrl,
                  },
                  {
                    label: t('organizations.properties.country'),
                    value: organizationQuery.data?.organization?.country,
                  },
                  {
                    label: t('organizations.properties.state'),
                    value: organizationQuery.data?.organization?.state,
                  },
                  {
                    label: t('organizations.properties.city'),
                    value: organizationQuery.data?.organization?.city,
                  },
                  {
                    label: t('organizations.properties.createdAt'),
                    value: datetimeHelper.formatDate(organizationQuery.data?.organization?.createdAt),
                  },
                  {
                    label: t('organizations.properties.lastUpdate'),
                    value: datetimeHelper.formatDate(organizationQuery.data?.organization?.lastUpdate),
                  },
                ]
              : []
          }
        />
      </Box>
    </CommonLayout>
  );
}
